/**
 * Move money (TMMAPP-0108, the tmm-api review amendment; MM-D-114; `POST
 * /v1/virtual-accounts/{id}/assign`): From (Unassigned or an envelope) → To (an envelope or
 * Unassigned), an amount and an optional note, at 520. Writers only. It moves money between
 * envelopes and Unassigned without touching any bank line's total. `409 INSUFFICIENT_TO_ASSIGN`
 * shows the served `details.availableMinor`. One `Idempotency-Key` per submit attempt; the page
 * refetches after a move rather than reading the response.
 */
import { useId, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { allocationsApi } from '@/api/allocations';
import type { Envelope } from '@/api/envelopes';
import { ErrorCodes, isApiError } from '@/api/errors';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { formatMoneyMinor, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { generateRequestId } from '@/utils/requestId';
import { availableOf, toSide, UNASSIGNED_SIDE } from './assignSides';

interface MoveMoneyDialogProps {
  virtualAccount: VirtualAccountDetail;
  envelopes: Envelope[];
  /** Where the dialog opened: an envelope card prefills To with it. */
  initialTo?: number;
  onClose: () => void;
  onMoved: () => void | Promise<void>;
  onSubscriptionRequired: () => void;
}

export function MoveMoneyDialog({
  virtualAccount,
  envelopes,
  initialTo,
  onClose,
  onMoved,
  onSubscriptionRequired,
}: MoveMoneyDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const subtitleId = useId();
  const initial = initialTo !== undefined ? String(initialTo) : (envelopes[0]?.id.toString() ?? '');
  const [from, setFrom] = useState<string>(UNASSIGNED_SIDE);
  const [to, setTo] = useState<string>(initial);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useRef<string | null>(null);
  const currency = virtualAccount.currency;
  const minor = parseMoneyToMinor(amount, currency, locale);
  const same = from === to;
  const valid = !same && to !== '' && minor !== null && minor > 0;
  const dirty = amount.trim().length > 0 || note.trim().length > 0;
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  const options = [
    <MenuItem key={UNASSIGNED_SIDE} value={UNASSIGNED_SIDE}>
      {t('virtualAccount.unassigned')}
    </MenuItem>,
    ...envelopes.map((envelope) => (
      <MenuItem key={envelope.id} value={String(envelope.id)}>
        {envelope.name}
      </MenuItem>
    )),
  ];

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!valid || submitting || minor === null) return;
    setSubmitting(true);
    setError(null);
    if (key.current === null) key.current = generateRequestId();
    try {
      await allocationsApi.assign(
        virtualAccount.id,
        {
          from: toSide(from),
          to: toSide(to),
          amountMinor: minor,
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        },
        key.current,
      );
      await onMoved();
      onClose();
    } catch (caught) {
      key.current = null;
      const code = isApiError(caught) ? caught.code : ErrorCodes.UNKNOWN;
      if (code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onClose();
        onSubscriptionRequired();
        return;
      }
      const available = availableOf(caught);
      setError(
        code === 'INSUFFICIENT_TO_ASSIGN' && available !== null
          ? t('virtualAccount.moveMoney.insufficient', {
              amount: formatMoneyMinor(available, currency, locale),
            })
          : code === 'INSUFFICIENT_TO_ASSIGN'
            ? t('allocations.errorInsufficientToAssign')
            : t('virtualAccount.errorGeneric'),
      );
      guard.focusErrors();
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      subtitle={
        <DialogSubtitle id={subtitleId}>
          {t('virtualAccount.moveMoney.sub', { name: virtualAccount.name })}
        </DialogSubtitle>
      }
      subtitleId={subtitleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 520 } }}
    >
      <Box
        component="form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => void handleSubmit(event)}
        noValidate
      >
        <DialogTitle id={titleId}>{t('virtualAccount.moveMoney.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error !== null && <Alert severity="error">{error}</Alert>}
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}
            >
              <FormControl fullWidth>
                <InputLabel id={`${titleId}-from`}>{t('virtualAccount.moveMoney.from')}</InputLabel>
                <Select
                  labelId={`${titleId}-from`}
                  label={t('virtualAccount.moveMoney.from')}
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                >
                  {options}
                </Select>
              </FormControl>
              <FormControl fullWidth error={same}>
                <InputLabel id={`${titleId}-to`}>{t('virtualAccount.moveMoney.to')}</InputLabel>
                <Select
                  labelId={`${titleId}-to`}
                  label={t('virtualAccount.moveMoney.to')}
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                >
                  {options}
                </Select>
              </FormControl>
            </Box>
            {same && (
              <Alert severity="warning" variant="outlined">
                {t('virtualAccount.moveMoney.same')}
              </Alert>
            )}
            <TextField
              label={t('virtualAccount.moveMoney.amount')}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              required
              error={amount.trim().length > 0 && (minor === null || minor <= 0)}
              slotProps={{
                htmlInput: { inputMode: 'decimal' },
                input: {
                  startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
                },
              }}
              sx={{ maxWidth: 240 }}
            />
            <TextField
              label={t('virtualAccount.moveMoney.note')}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" disabled={!valid} loading={submitting}>
            {t('virtualAccount.moveMoney.action')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </GuardedDialog>
  );
}
